import { Card, CardContent, CardHeader, CardTitle } from "@wondermaps/ui";

interface BehaviorData {
  repeatVisitorPct: number;
  totalUniqueVisitors: number;
  repeatVisitors: number;
  peakHours: { hour: number; count: number }[];
}

const BAR_MAX_HEIGHT = 120; // px

export function BehaviorRetention({ data, timezone }: { data: BehaviorData; timezone: string }) {
  const maxHourCount = Math.max(...data.peakHours.map((h) => h.count), 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Visitor Behavior & Retention</CardTitle>
        <p className="text-sm text-muted-foreground">
          Measures how many visitors return to the map more than once. A high
          repeat rate signals ongoing utility. Peak hours help clients staff
          and schedule around actual demand.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Retention stats */}
        <div className="grid grid-cols-3 gap-4">
          <StatBox
            label="Unique Visitors"
            value={data.totalUniqueVisitors.toLocaleString()}
          />
          <StatBox
            label="Repeat Visitors"
            value={data.repeatVisitors.toLocaleString()}
          />
          <StatBox
            label="Repeat Rate"
            value={`${data.repeatVisitorPct}%`}
            highlight
          />
        </div>

        {/* Peak hours chart */}
        {data.peakHours.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-3">
              Peak Hours (Map Views)
              <span className="text-xs text-muted-foreground font-normal ml-2">
                {timezone}
              </span>
            </h4>
            <div className="flex items-end gap-[2px]" style={{ height: BAR_MAX_HEIGHT + 32 }}>
              {Array.from({ length: 24 }, (_, h) => {
                const entry = data.peakHours.find((p) => p.hour === h);
                const count = entry?.count ?? 0;
                const barH = maxHourCount > 0
                  ? Math.max(Math.round((count / maxHourCount) * BAR_MAX_HEIGHT), count > 0 ? 4 : 1)
                  : 1;
                return (
                  <div
                    key={h}
                    className="flex-1 flex flex-col items-center justify-end"
                    style={{ height: BAR_MAX_HEIGHT + 32 }}
                  >
                    {count > 0 && (
                      <span className="text-[8px] text-muted-foreground mb-0.5 tabular-nums">
                        {count}
                      </span>
                    )}
                    <div
                      className="w-full rounded-t bg-primary/80"
                      style={{ height: barH }}
                      title={`${formatHour(h)}: ${count} views`}
                    />
                    <span className="text-[8px] text-muted-foreground mt-1 tabular-nums">
                      {formatHour(h)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatBox({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-lg border p-3 text-center">
      <p className={`text-2xl font-bold ${highlight ? "text-primary" : ""}`}>
        {value}
      </p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function formatHour(h: number): string {
  if (h === 0) return "12a";
  if (h === 12) return "12p";
  return h < 12 ? `${h}a` : `${h - 12}p`;
}
